<template>
    <section class="text-left" :class="{ 'border-t border-gray-200 pt-10 dark:border-default': bordered }">
        <h2 class="mb-2 text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">{{ title }}</h2>
        <p v-if="description" class="mb-6 max-w-3xl text-gray-600 dark:text-gray-300">{{ description }}</p>

        <!-- Compact pills, e.g. regions or cities -->
        <ul v-if="variant === 'chips'" class="flex flex-wrap gap-2" :class="{ 'mt-4': !description }">
            <li v-for="link in links" :key="link.label" class="!mb-0">
                <NuxtLink
                    :to="link.to"
                    class="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50 dark:border-default dark:text-gray-300 dark:hover:bg-subtle"
                    @click="scrollUpOnSamePage(link.to)"
                >
                    <CIcon v-if="link.icon" :name="link.icon" class="size-4 text-gray-500 dark:text-gray-400" />
                    {{ link.label }}
                </NuxtLink>
            </li>
        </ul>

        <!-- Cards with a section icon and a one-line explanation -->
        <ul
            v-else
            class="grid gap-3 sm:grid-cols-2"
            :class="[cardColumnsClass, { 'mt-4': !description }]"
        >
            <li v-for="link in links" :key="link.label" class="!mb-0 !block">
                <NuxtLink
                    :to="link.to"
                    class="flex h-full items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-default dark:bg-surface-card dark:hover:bg-subtle"
                    @click="scrollUpOnSamePage(link.to)"
                >
                    <span
                        v-if="link.section"
                        class="flex size-10 shrink-0 items-center justify-center rounded-full"
                        :class="sectionStyles[link.section].bgClass"
                    >
                        <CIcon
                            :name="sectionStyles[link.section].icon"
                            class="size-5"
                            :class="sectionStyles[link.section].iconClass"
                        />
                    </span>
                    <span class="min-w-0">
                        <span class="block font-semibold text-gray-900 dark:text-white">{{ link.label }}</span>
                        <span v-if="link.description" class="mt-0.5 block text-sm text-gray-600 dark:text-gray-300">
                            {{ link.description }}
                        </span>
                    </span>
                </NuxtLink>
            </li>
        </ul>
    </section>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

/**
 * Section icons and colors, mirroring the nav bar (HeaderNew.vue), including its lighter
 * mentoring teal in dark mode. Full class strings so Tailwind picks them up.
 */
const sectionStyles = {
    mentoring: {
        icon: 'mentoring',
        iconClass: 'text-[#047274] dark:text-[#4AD8DC]',
        bgClass: 'bg-[rgba(4,114,116,0.1)] dark:bg-[rgba(74,216,220,0.1)]'
    },
    volunteering: { icon: 'volunteering', iconClass: 'text-[#eab308]', bgClass: 'bg-[rgba(234,179,8,0.1)]' },
    jobs: { icon: 'jobs', iconClass: 'text-[#f97316]', bgClass: 'bg-[rgba(249,115,22,0.1)]' },
    skills: { icon: 'skills', iconClass: 'text-[#c24cfc]', bgClass: 'bg-[rgba(194,76,252,0.1)]' },
    selfKnowledge: { icon: 'head', iconClass: 'text-[#ec4899]', bgClass: 'bg-[rgba(236,72,153,0.1)]' },
    organizations: { icon: 'building', iconClass: 'text-[#f97316]', bgClass: 'bg-[rgba(249,115,22,0.1)]' },
    circles: { icon: 'group', iconClass: 'text-[#40b153]', bgClass: 'bg-[rgba(64,177,83,0.1)]' },
    discussions: { icon: 'messages', iconClass: 'text-gray-600 dark:text-gray-300', bgClass: 'bg-gray-100 dark:bg-subtle' }
} as const

export type ListingSeoLinkSection = keyof typeof sectionStyles

export interface ListingSeoLink {
    to: RouteLocationRaw
    label: string
    description?: string
    /** Nav-bar section whose icon and color the card uses (cards variant). */
    section?: ListingSeoLinkSection
    /** Optional growni icon shown in a chip (chips variant). */
    icon?: string
}

const props = withDefaults(
    defineProps<{
        title: string
        description?: string
        links: ListingSeoLink[]
        variant?: 'cards' | 'chips'
        /** Top border separating the block from the results; off for follow-up groups. */
        bordered?: boolean
        /** Cap the card grid at two columns, for narrow content columns. */
        narrow?: boolean
    }>(),
    {
        description: undefined,
        variant: 'cards',
        bordered: true,
        narrow: false
    }
)

const cardColumnsClass = computed(() => {
    if (props.narrow) return ''
    return props.links.length % 4 === 0 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
})

const route = useRoute()
const router = useRouter()

/**
 * Links to a filtered view of the current listing only change the query, and Nuxt keeps the
 * scroll position for same-path navigation, so the reader would stay down here while the
 * results above change. Bring them back up to the results.
 */
function scrollUpOnSamePage(to: RouteLocationRaw) {
    if (import.meta.client && router.resolve(to).path === route.path) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
}
</script>
